import { useCallback } from 'react';
import { useMutation } from 'react-relay';
import type {
  Disposable,
  GraphQLTaggedNode,
  UseMutationConfig,
} from 'react-relay';
import type { MutationParameters } from 'relay-runtime';
import {
  useOptionalPortalInteractionLock,
  usePortalInteractionLock,
} from './PortalInteractionLockContext';
import type { PortalInteractionLockContextValue } from './PortalInteractionLockContext';

const useInteractionLockMutation = <TMutation extends MutationParameters>(
  mutation: GraphQLTaggedNode,
  status: string,
  interactionLock: PortalInteractionLockContextValue | null
) => {
  const [commitMutation, isMutationInFlight] = useMutation<TMutation>(mutation);
  const beginInteraction = interactionLock?.beginInteraction;
  const finishInteraction = interactionLock?.endInteraction;

  const commitPortalMutation = useCallback(
    (config: UseMutationConfig<TMutation>): Disposable => {
      if (!beginInteraction || !finishInteraction)
        return commitMutation(config);

      const interactionId = beginInteraction(status);
      let interactionEnded = false;
      const endInteraction = () => {
        if (interactionEnded) return;
        interactionEnded = true;
        finishInteraction(interactionId);
      };

      try {
        const disposable = commitMutation({
          ...config,
          onCompleted: (response, errors) => {
            endInteraction();
            config.onCompleted?.(response, errors);
          },
          onError: (error) => {
            endInteraction();
            config.onError?.(error);
          },
          onUnsubscribe: () => {
            endInteraction();
            config.onUnsubscribe?.();
          },
        });

        return {
          dispose: () => {
            disposable.dispose();
            endInteraction();
          },
        };
      } catch (error) {
        endInteraction();
        throw error;
      }
    },
    [beginInteraction, commitMutation, finishInteraction, status]
  );

  return [commitPortalMutation, isMutationInFlight] as const;
};

export const usePortalMutation = <TMutation extends MutationParameters>(
  mutation: GraphQLTaggedNode,
  status: string
) => {
  const interactionLock = usePortalInteractionLock();
  return useInteractionLockMutation<TMutation>(
    mutation,
    status,
    interactionLock
  );
};

export const useOptionalPortalMutation = <TMutation extends MutationParameters>(
  mutation: GraphQLTaggedNode,
  status: string
) => {
  const interactionLock = useOptionalPortalInteractionLock();
  return useInteractionLockMutation<TMutation>(
    mutation,
    status,
    interactionLock
  );
};
